// src/api/axios/interceptors.ts
import { notify } from '@/components/ui/ui-react-antd/NotificationCustom'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'

import axios from 'axios'
import { ENV_CONFIG } from '../../utils/env'
import { getAuthToken } from '../../utils/storage'
import { ensureValidToken, isAuthError, retryWithFreshToken } from './authSession'

// Extract error message từ response envelope (object / errorMessage / message)
const extractErrorMessage = (data: any, fallbackMessage: string): string => {
  if (!data) return fallbackMessage
  if (typeof data === 'string') return data
  const msg =
    (typeof data.object === 'string' ? data.object : null) ??
    (typeof data.Object === 'string' ? data.Object : null) ??
    data.errorMessage ??
    data.ErrorMessage ??
    data.message ??
    data.Message
  return typeof msg === 'string' && msg.trim() ? msg : fallbackMessage
}

type AuthRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
  _skipAuth?: boolean
  _networkRetry?: boolean
}

// ============ AXIOS INTERCEPTORS ============
export const setupInterceptors = (instance: AxiosInstance) => {
  // Request Interceptor - Gắn token vào header
  instance.interceptors.request.use(
    (config) => {
      if (config.data instanceof FormData) {
        config.headers['Content-Type'] = undefined
      }
      const token = getAuthToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // Response Interceptor - Xử lý response và refresh token
  instance.interceptors.response.use(
    async (response) => {
      // Blob response trả về nguyên vẹn
      if (response.config.responseType === 'blob') {
        return response
      }

      // ponytail: BE trả envelope không nhất quán casing (camelCase hoặc PascalCase tuỳ endpoint)
      const isError = response.data.isError
      const statusCode = Number(response.data.statusCode ?? response.data.StatusCode)
      const originalRequest = response.config as AuthRequestConfig

      // Xử lý HTTP 403 Forbidden (business-level, trong envelope)
      if (statusCode === 403) {
        notify.error('Bạn không có quyền thực hiện thao tác này.')
        return Promise.reject({
          errorCode: 403,
          errorMessage: 'Forbidden',
          config: response.config,
        })
      }

      // Xử lý 401 Unauthorized — refresh + retry một lần (chống loop bằng _retry)
      if (
        isAuthError(statusCode, response.data) &&
        !originalRequest._retry &&
        !originalRequest._skipAuth &&
        !originalRequest.url?.includes('ChangePassword')
      ) {
        return retryWithFreshToken(originalRequest, instance)
      }

      // Xử lý lỗi từ API (không phải HTTP error)
      if (isError && statusCode !== 200) {
        const errorText = extractErrorMessage(response.data, 'Lỗi từ server')
        if (statusCode !== 401) {
          notify.error(errorText)
        }
        return Promise.reject({
          errorCode: statusCode,
          errorMessage: errorText,
          message: errorText,
          config: response.config,
        })
      }

      return response.data
    },

    async (error) => {
      const originalRequest = error.config as AuthRequestConfig

      // Network error / timeout (không có response) — kết nối cũ có thể đã bị NAT/router
      // âm thầm drop trong lúc tab idle (browser không biết, chỉ phát hiện khi gửi request
      // và chờ tới OS-level TCP timeout → ERR_CONNECTION_TIMED_OUT). Thử lại 1 lần trên
      // connection mới trước khi báo lỗi cho user.
      if (!error.response && !originalRequest?._networkRetry) {
        originalRequest._networkRetry = true
        return instance(originalRequest)
      }

      // Xử lý HTTP 403 Forbidden
      if (error.response?.status === 403) {
        notify.error('Bạn không có quyền thực hiện thao tác này.')
        return Promise.reject(error)
      }

      // Xử lý HTTP 401 Unauthorized — refresh + retry một lần (chống loop bằng _retry)
      if (
        isAuthError(error.response?.status, error.response?.data) &&
        !originalRequest._retry &&
        !originalRequest._skipAuth &&
        !originalRequest.url?.includes('ChangePassword')
      ) {
        return retryWithFreshToken(originalRequest, instance)
      }

      // Các lỗi HTTP khác
      const errorMessage = extractErrorMessage(
        error.response?.data,
        error.message || 'Lỗi không xác định từ server'
      )
      error.message = errorMessage
      notify.error(errorMessage)
      return Promise.reject(error)
    }
  )
}

// ============ AXIOS INSTANCE ============
const instance = axios.create({
  baseURL: ENV_CONFIG.VITE_BACKEND_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
})

setupInterceptors(instance)

// Backward-compat: giữ export để không phá test/usage hiện có
export const refreshTokenAction = async (): Promise<{
  success: boolean
  accessToken?: string
}> => {
  try {
    const accessToken = await ensureValidToken()
    return { success: true, accessToken }
  } catch {
    return { success: false }
  }
}

export default instance
